import { SxProps } from "@mui/material";
import { useContext, useEffect, useRef } from "react";
import { roughlyEqual } from "utils/numbers";
import TimelinePlayerContext from "../context/TimelinePlayerContext";
import TransitionPlayer_Content from "./TransitionPlayer_Content";

interface Props {
  playing?: boolean;
  onEnded?: () => void;
  sx?: SxProps;
}

const INCREMENT_INTERVAL_IN_MS = 100;

export default function TransitionPlayer({ onEnded, playing, sx }: Props) {
  const {
    absoluteTime,
    comment,
    endTime,
    playbackRate,
    setAbsoluteTime,
    timelineItemIndex,
    timelinePositions,
  } = useContext(TimelinePlayerContext);

  const incrementIsScheduled = useRef<boolean>(false);
  const timerRef = useRef<NodeJS.Timeout | undefined>(undefined);

  const absoluteStart = [...timelinePositions.keys()][timelineItemIndex];
  const absoluteEnd = absoluteStart + (endTime || 0);

  function clearTimer() {
    timerRef.current && clearTimeout(timerRef.current);
    incrementIsScheduled.current = false;
  }

  useEffect(() => {
    if (!playing) {
      clearTimer();
      return;
    }
    if (incrementIsScheduled.current) return;

    const isAtPreviousTimelineItem =
      absoluteTime < absoluteStart &&
      !roughlyEqual(absoluteTime, absoluteStart);
    if (isAtPreviousTimelineItem) {
      clearTimer();
      return;
    }

    const isAtTheEnd =
      roughlyEqual(absoluteTime, absoluteEnd) || absoluteTime > absoluteEnd;
    if (isAtTheEnd) {
      clearTimer();
      onEnded?.();
      return;
    }

    // Queue up the next increment
    const nextAbsoluteTime =
      absoluteTime + (INCREMENT_INTERVAL_IN_MS / 1000) * playbackRate;
    timerRef.current = setTimeout(() => {
      setAbsoluteTime(nextAbsoluteTime);
      incrementIsScheduled.current = false;
    }, INCREMENT_INTERVAL_IN_MS);
    incrementIsScheduled.current = true;

    return clearTimer;
  }, [absoluteTime, playing, timelineItemIndex]);

  return (
    <TransitionPlayer_Content
      absoluteTime={absoluteTime}
      absoluteEnd={absoluteEnd}
      absoluteStart={absoluteStart}
      sx={{
        maxHeight: "100%",
        maxWidth: "100%",
        minHeight: "100%",
        minWidth: "100%",
        ...sx,
      }}
    >
      {comment}
    </TransitionPlayer_Content>
  );
}
